import { Empty } from 'antd'
import TrainingFilters from 'components/Filters/TrainingFilters'
import HighLight from 'components/HighLight'
import Collapse from 'components/UI/Collapse'
import Button from 'components/UI/CoreUIButton'
import SearchInput from 'components/UI/SearchInput'
import Section from 'components/UI/Section'
import Spin from 'components/UI/Spin'
import { API_TRAININGS } from 'constants/api-v2'
import { useI18n } from 'context/I18nProvider'
import useFilters from 'hooks/filters'
import { useSWRInfiniteScroll } from 'hooks/swr'
import { useTableState } from 'hooks/useTableState'
import { useLocation, useRouteLoaderData } from 'react-router-dom'
import type { Training as TrainingType, UserData } from 'shared/types'
import { messageContext } from 'utils/string'
import Card from '../components/Card'

const PAGE_SIZE = 30

const Path = ({ category, theme }: { category: string; theme: string }) => {
  const separator = category && theme ? ' - ' : ''
  return (
    <>
      <HighLight title={category}>{category}</HighLight>
      {separator}
      <HighLight title={theme}>{theme}</HighLight>
    </>
  )
}

export default function Training() {
  const { i18n, i18name } = useI18n()
  const { defaultVintage } = useRouteLoaderData('user') as UserData
  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const search = searchParams.get('q')

  const { setCurrent } = useTableState({
    defaultPageSize: PAGE_SIZE,
    defaultOrderBy: 'user.fullName',
  })

  const filtersProps = useFilters(
    'training',
    true,
    {
      ...(defaultVintage.training
        ? {
            vintage: [
              {
                value: defaultVintage.training.id.toString(),
                label: i18name(defaultVintage.training),
              },
            ],
          }
        : {}),
    },
    () => setCurrent(1),
  )
  const { values, setValue, url: filtersUrl, isInitialized } = filtersProps
  const params =
    '?groups[]=trainings:list:cards&status[]=validated&visibleInCatalog=true&fictive=false&translations.name=asc'
  const pagination = `&pagination=true&itemsPerPage=${PAGE_SIZE}`
  const url = `${API_TRAININGS}${params}${pagination}${filtersUrl ? `&${filtersUrl}` : ''}`

  const { data, total, size, setSize, isValidating } = useSWRInfiniteScroll(url)
  const trainings = (data || []) as TrainingType[]
  const lastPage = total > 0 ? Math.ceil(total / PAGE_SIZE) : 0
  const hasNext = total > 0 && size < lastPage
  const next = () => setSize(size + 1)
  const initialLoading = isValidating && trainings.length === 0
  const loadingMore = isValidating && trainings.length > 0

  if (!isInitialized) {
    return null
  }

  const empty = !trainings.length
  const { reset, ...restFiltersProps } = filtersProps

  const filterCollapseItems = [
    {
      key: '1',
      label: (
        <div className="text-gray-600 flex gap-2">
          <span className="uppercase font-semibold">{i18n('label.filters')}</span>
          <span>{i18n('catalog.refine.text')}</span>
        </div>
      ),
      children: <TrainingFilters {...restFiltersProps} />,
      extra: (
        <Button
          leftIcon="arrow-rotate-left"
          variant="tertiary"
          onClick={e => {
            e.stopPropagation()
            reset()
          }}
        >
          {i18n('label.reinit')}
        </Button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-4">
      <SearchInput
        defaultValue={search ?? ''}
        value={values.q || ''}
        onChange={txt => setValue('q', txt)}
        debounceTime={300}
        placeholder={i18n('form.trainings_management_search_placeholder')}
        suffix={`${total} ${i18n('catalog.trainings.available')}`}
      />
      <Collapse items={filterCollapseItems} />

      <div className="grid grid-cols-4 gap-6 mt-4">
        {!empty &&
          trainings.map((training: TrainingType) => (
            <Card
              key={training['@id']}
              training={training}
              highlight={values?.q?.toString()}
              path={<Path category={i18name(training.category)} theme={i18name(training.theme)} />}
            />
          ))}
        {initialLoading && (
          <div className="col-span-4 flex justify-center">
            <Spin size="middle" />
          </div>
        )}
        {!initialLoading && empty && (
          <Section className="col-span-4">
            <Empty />
          </Section>
        )}
      </div>
      {!empty && hasNext && (
        <Button onClick={next} className=" self-center" loading={loadingMore}>
          {messageContext(i18n('label.infinite.scroll.button'), { nbPerPage: PAGE_SIZE })}
        </Button>
      )}
    </div>
  )
}
